import * as React from 'react'
import PropTypes from "prop-types";

/**
 * Here are some scenarios to keep in mind with rounding:
    +-----------------+----------+-------+--+-----------------+----------+-------+
    |                 | Percent  |  Sum  |  |                 | Percent  |  Sum  |
    +-----------------+----------+-------+--+-----------------+----------+-------+
    | Raw             |     6.74 |  6.74 |  | Raw             |     6.49 |  6.49 |
    |                 |    20.51 | 27.25 |  |                 |    20.49 | 26.98 |
    |                 |    72.75 |   100 |  |                 |    73.02 |   100 |
    |                 |          |       |  |                 |          |       |
    | Normal Rounding |        7 |     7 |  | Normal Rounding |        6 |     6 |
    |                 |       21 |    28 |  |                 |       20 |    26 |
    |                 |       73 |   101 |  |                 |       73 |    99 |
    |                 |          |       |  |                 |          |       |
    | Down            |        6 |     6 |  | Down            |        6 |     6 |
    |                 |       20 |    26 |  |                 |       20 |    26 |
    |                 |       72 |    98 |  |                 |       72 |    98 |
    |                 |          |       |  |                 |          |       |
    | Up              |        7 |     7 |  | Up              |        7 |     7 |
    |                 |       21 |    28 |  |                 |       21 |    28 |
    |                 |       73 |   101 |  |                 |       73 |   101 |
    +-----------------+----------+-------+--+-----------------+----------+-------+
 */
const roundToN = (value, n, roundDown, roundUp) => {
  const pow = Math.pow(10, n);
  const sub = value * pow;
  const rounded = roundUp ? Math.ceil(sub) : roundDown ? Math.floor(sub) : Math.round(sub);
  return rounded / pow
};

const numberAbbreviator = (number) => {
  const parsed = parseInt(number);
  const abs = Math.abs(parsed);

  if (abs > 10000000) {
    return (parsed / 1000000).toFixed(0) + "M"
  } else if (abs > 1000000) {
    return (parsed / 1000000).toFixed(1) + "M"
  } else if (abs > 10000) {
    return (parsed / 1000).toFixed(0) + "K"
  } else if (abs > 1000) {
    return (parsed / 1000).toFixed(1) + "K"
  } else {
    return parsed
  }
};

/**
 * Welcome to PurtyNumber, this will make your numbers pur!
 */
export class PurtyNumber extends React.Component {

  static propTypes = {
    // The input value
    value: PropTypes.number,

    /**
     * Whether or not the input value should be interpreted as a percent. Value should be decimal
      * 1 is displayed as 100%
      */
    percent: PropTypes.bool,

    /*
     * To display commas or not
     * If large format is selected, this will be disabled
     */
    commas: PropTypes.bool,

    /*
     * To display the absolute value of the value
     */
    abs: PropTypes.bool,

    /*
     * Whether to display a simple format for larger numbers >= 1,000
     * i.e.   100,000 -> 100K
     */
    largeFormat: PropTypes.bool,

    // How many decimal places you'd like
    numberOfDecimalPlaces: PropTypes.number,

    // Class selector
    classSelector: PropTypes.func,

    // The color of text to display
    colorClass: PropTypes.any,

    // A default value to show in the case of a null/undefined value
    defaultValue: PropTypes.any,

    /*
     * Text to display following the number.
     * Note that if this text will be appended after a '%' if you have percent enabled
     */
    descriptionText: PropTypes.any,

    /*
     * object to display after the number.
     */
    suffix: PropTypes.func,

    /*
     * threshold for which to switch to large number formatting.
     */
    switchToLargeThreshold: PropTypes.number,

    // Round to floor, round up will take precedence over round down if both provided
    roundDown: PropTypes.bool,

    // Round to ceil, round up will take precedence over round down if both provided
    roundUp: PropTypes.bool
  };

  static defaultProps = {
    commas: true,
    abs: false,
    percent: false,
    largeFormat: false,
    roundDown: false,
    roundUp: false,
    numberOfDecimalPlaces: 0,
    switchToLargeThreshold: 1000000
  };

  render() {

    const { value, commas, percent, numberOfDecimalPlaces, colorClass, defaultValue, classSelector, descriptionText, suffix } = this.props;
    const largeFormat = this.props.largeFormat || Math.abs(value) >= this.props.switchToLargeThreshold;

    const clazz =
      [
        classSelector ? classSelector(value) : undefined,
        colorClass
      ].filter(x => x !== undefined)
        .join(' ')

    const absolute = (value !== undefined && this.props.abs) ? Math.abs(value) : value;
    const rounded = value !== undefined && roundToN((percent ? absolute * 100.0 : absolute), numberOfDecimalPlaces, this.props.roundDown, this.props.roundUp);
    const cleaned = value !== undefined && (commas && !largeFormat ? rounded.toLocaleString(undefined, { minimumFractionDigits: numberOfDecimalPlaces }) : (largeFormat ? numberAbbreviator(rounded) : rounded));
    const displayText = value !== undefined ? (!!percent ? cleaned + "%" : cleaned) : defaultValue;

    return (
      <React.Fragment>
        {
          <span className={clazz}>
            <span>{displayText}{!!descriptionText && ` ${descriptionText}`}</span>
            {!!suffix && suffix(value)}
          </span>
        }
      </React.Fragment>)
  }

}


